import type {
  DeviceResourceDiagnosticsListResponseApi,
  EnumDeviceDiagnosticsTypeApi,
  EnumDiagnosticTestExecutionStatusApi,
  EnumDiagnosticTestModuleApi,
  EnumDiagnosticTestStatusApi,
  GetDeviceDiagnosticByDeviceIdAndDiagnosticIdV2QueryResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { toString } from 'lodash-es';
import type {
  DiagnosticTestModule,
  DiagnosticTestStatus,
  MappedDiagnosticsResourceApi,
  MappedDiagnosticsResourceListResponseApi,
} from '../../models/DeviceDiagnosticsDetails.model';
import {
  DeviceDiagnosticsType,
  DiagnosticTestExecutionStatus,
  enumDiagnosticTestModule,
  enumDiagnosticTestStatus,
} from '../../models/DeviceDiagnosticsDetails.model';

function _ensureIncomingDiagnosticTestExecutionStatusIsValid(
  incomingTestExecutionStatus: EnumDiagnosticTestExecutionStatusApi,
): DiagnosticTestExecutionStatus {
  const isValid = Object.values(DiagnosticTestExecutionStatus).includes(
    incomingTestExecutionStatus as DiagnosticTestExecutionStatus,
  );
  return isValid ? (incomingTestExecutionStatus as DiagnosticTestExecutionStatus) : DiagnosticTestExecutionStatus.Error;
}

function _ensureIncomingDiagnosticTestModuleIsValid(
  incomingTestModule: EnumDiagnosticTestModuleApi,
): DiagnosticTestModule {
  const isValid = Object.values(enumDiagnosticTestModule).includes(incomingTestModule as DiagnosticTestModule);
  return isValid ? (incomingTestModule as DiagnosticTestModule) : enumDiagnosticTestModule.AUDIO;
}

function ensureIncomingDiagnosticTestStatusIsValid(
  incomingTestStatus: EnumDiagnosticTestStatusApi,
): DiagnosticTestStatus {
  const isValid = Object.values(enumDiagnosticTestStatus).includes(incomingTestStatus as DiagnosticTestStatus);
  return isValid ? (incomingTestStatus as DiagnosticTestStatus) : enumDiagnosticTestStatus.NOT_APPLICABLE;
}

function ensureIncomingDiagnosticTypeIsValid(incomingType: EnumDeviceDiagnosticsTypeApi): DeviceDiagnosticsType {
  const isValid = Object.values(DeviceDiagnosticsType).includes(incomingType as DeviceDiagnosticsType);
  return isValid ? (incomingType as DeviceDiagnosticsType) : DeviceDiagnosticsType.AutomaticDiagnostics;
}

export function mapDeviceDiagnosticsResponseApiToPaginatedDeviceDiagnosticsList(
  response: DeviceResourceDiagnosticsListResponseApi,
): MappedDiagnosticsResourceListResponseApi {
  return {
    ...response,
    data: response?.data?.map((diagnostic) => ({
      date: diagnostic.date,
      deviceId: diagnostic.id, // Usando o id como deviceId temporariamente
      serialNumber: toString(diagnostic.serialNumber),
      id: toString(diagnostic.id),
      testResults: diagnostic.testResults.map((testResult) => ({
        resultCode: testResult.resultCode || 'UNKNOWN',
        errorCode: testResult.resultCode || 'UNKNOWN',
        executionStatus: testResult.executionStatus,
        module: testResult.module,
        status: ensureIncomingDiagnosticTestStatusIsValid(testResult.status),
      })),
      type: ensureIncomingDiagnosticTypeIsValid(diagnostic.type),
    })),
  };
}

export function mapDeviceDiagnosticsResponseApiToDeviceDiagnosticsDetails(
  response: GetDeviceDiagnosticByDeviceIdAndDiagnosticIdV2QueryResponseApi,
): MappedDiagnosticsResourceApi {
  return {
    date: response.date,
    deviceId: response.id, // Usando o id como deviceId temporariamente
    serialNumber: toString(response.serialNumber),
    id: toString(response.id),
    testResults: response.testResults.map((testResult) => ({
      resultCode: testResult.resultCode || 'UNKNOWN',
      errorCode: testResult.resultCode || 'UNKNOWN',
      executionStatus: testResult.executionStatus,
      module: testResult.module,
      status: ensureIncomingDiagnosticTestStatusIsValid(testResult.status),
    })),
    type: ensureIncomingDiagnosticTypeIsValid(response.type),
  };
}
